"use client"

import React, { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import api from "../../../libs/axios"
import Loader from "../../../utils/loader"
import Breadcrumb from "../../../utils/breadcrumbs"
import Pagination from "../../../utils/pagination2"
import { FiPlus, FiTrash2, FiEdit } from "react-icons/fi"
import ConfirmationModal from "../../../ui/confirmation-modal"
import EditTemplateOffcanvas from "../../../components/notifications/edit-template-offcanvas"
import CreateTemplateOffcanvas from "../../../components/notifications/create-template-offcanvas"

export interface INotificationTemplate {
    id: number
    name: string
    subject: string
    message: string
    type: string
    status: string
    created_at?: Date
    updated_at?: Date
}

const NotificationTemplates: React.FC = () => {
    const navigate = useNavigate()
    const [selectedTemplate, setSelectedTemplate] = useState<INotificationTemplate | null>(null)
    const [searchParams, setSearchParams] = useState<Record<string, string>>({})
    const [showAddTemplateCanvas, setShowAddTemplateCanvas] = useState(false)
    const [showEditTemplateCanvas, setShowEditTemplateCanvas] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [templates, setTemplates] = useState<INotificationTemplate[]>([])
    const [loading, setLoading] = useState(false)

    // Pagination state variables
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    });

    // Filter templates based on search parameters
    const filteredTemplates = useMemo(() => {
        return templates.filter((template) => {
            const nameMatch = !searchParams.name || template.name.toLowerCase().includes(searchParams.name.toLowerCase())

            const typeMatch = !searchParams.type || template.type.toLowerCase() === searchParams.type.toLowerCase()

            const statusMatch = !searchParams.status || template.status.toLowerCase() === searchParams.status.toLowerCase()

            return nameMatch && typeMatch && statusMatch
        })
    }, [templates, searchParams])

    // Fetch templates - using API calls
    const fetchTemplates = async () => {
        setLoading(true)
        try {
            const params: Record<string, string | number> = {
                page: currentPage,
                limit,
                ...(searchParams.name ? { name: searchParams.name } : {}),
                ...(searchParams.subject ? { subject: searchParams.subject } : {}),
                ...(searchParams.type ? { type: searchParams.type } : {}),
                ...(searchParams.status ? { status: searchParams.status } : {}),
            }
            const res = await api.get("/notifications/templates", { params })
            // Expecting { data, pagination } shape following backend utils
            setTemplates(res.data.data || [])
            setPagination(res.data.pagination)
        } catch (e) {
            toast.error("Error fetching templates")
        } finally {
            setLoading(false)
        }
    }

    // Fetch templates
    React.useEffect(() => {
        fetchTemplates()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, limit, searchParams])

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams((prev) => {
            const newParams = { ...prev }
            if (value) {
                newParams[column] = value
            } else {
                delete newParams[column]
            }
            return newParams
        })
        setCurrentPage(1)
    }

    // Get status style
    const getStatusStyle = (status: string) => {
        switch (status.toLowerCase()) {
            case "active":
                return "bg-green-100 text-green-800"
            case "inactive":
                return "bg-red-100 text-red-800"
            case "draft":
                return "bg-yellow-100 text-yellow-800"
            default:
                return "bg-gray-100 text-gray-800"
        }
    }

    // Get type style
    const getTypeStyle = (type: string) => {
        switch (type.toLowerCase()) {
            case "email":
                return "bg-blue-100 text-blue-800"
            case "sms":
                return "bg-purple-100 text-purple-800"
            case "push":
                return "bg-orange-100 text-orange-800"
            default:
                return "bg-gray-100 text-gray-800"
        }
    }

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    // Handle delete
    const handleDeleteClick = (template: INotificationTemplate) => {
        setSelectedTemplate(template)
        setDeleteConfirmOpen(true)
    }

    // Handle edit
    const handleEditClick = (template: INotificationTemplate) => {
        setSelectedTemplate(template)
        setShowEditTemplateCanvas(true)
    }

    // Handle confirm delete
    const handleDeleteConfirm = async () => {
        if (selectedTemplate) {
            try {
                await api.delete(`/notifications/templates/${selectedTemplate.id}`)
                toast.success(`${selectedTemplate.name} deleted successfully!`)
                // refresh list
                fetchTemplates()
            } catch (err) {
                toast.error("Error deleting template")
            }
            setDeleteConfirmOpen(false)
            setSelectedTemplate(null)
        }
    }

    // Handle template created
    const handleTemplateCreated = (newTemplate: INotificationTemplate) => {
        setTemplates((prev) => [...prev, newTemplate])
    }

    // Handle template updated
    const handleTemplateUpdated = (updatedTemplate: INotificationTemplate) => {
        setTemplates((prev) => prev.map((template) => (template.id === updatedTemplate.id ? updatedTemplate : template)))
    }

    return (
        <div className="w-full min-h-screen bg-white p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Notification Templates
                </h2>
                <Breadcrumb />
            </div>
            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
                    <button
                        onClick={() => setShowAddTemplateCanvas(true)}
                        className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center sm:px-4"
                    >
                        <FiPlus className="w-5 h-5 sm:mr-0" />
                        <span className="hidden sm:block">Add Template</span>
                    </button>
                </div>

                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Template Name
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Subject
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Type
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Template Name"
                                        value={searchParams.name || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("name", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Subject"
                                        value={searchParams.subject || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("subject", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.type || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("type", e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="email">Email</option>
                                        <option value="sms">SMS</option>
                                        <option value="push">Push</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.status || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("status", e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="draft">Draft</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                            </tr>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="text-center">
                                        <Loader />
                                    </td>
                                </tr>
                            ) : filteredTemplates.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-8 text-gray-500">
                                        No templates found
                                    </td>
                                </tr>
                            ) : (
                                filteredTemplates.map((template) => (
                                    <tr key={template.id} className="hover:bg-gray-50 cursor-pointer">
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="flex font-semibold items-center">{template.name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="max-w-xs truncate">{template.subject}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getTypeStyle(template.type)}`}>
                                                {template.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getStatusStyle(template.status)}`}>
                                                {template.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex space-x-3" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => handleEditClick(template)}
                                                    className="text-primary-600 hover:text-primary-700"
                                                >
                                                    <FiEdit className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => handleDeleteClick(template)} className="text-red-600 hover:text-red-800">
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                </div>

                <CreateTemplateOffcanvas
                    showCreateTemplateCanvas={showAddTemplateCanvas}
                    setShowCreateTemplateCanvas={setShowAddTemplateCanvas}
                    onTemplateCreated={handleTemplateCreated}
                />

                {selectedTemplate && (
                    <EditTemplateOffcanvas
                        templateId={selectedTemplate.id.toString()}
                        showEditTemplateCanvas={showEditTemplateCanvas}
                        setShowEditTemplateCanvas={setShowEditTemplateCanvas}
                        onTemplateUpdated={handleTemplateUpdated}
                    />
                )}

                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete Template"
                        description="Are you sure you want to delete this template? This action cannot be undone."
                        loading={loading}
                    />
                )}
            </div>
        </div>
    )
}

export default NotificationTemplates
